import React, { Fragment, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { getSignedS3ResourceUrl } from 'chameleon-src/utility/signedUrl';
import {
  Icon as LegacyIcon,
  AccessibilityWrapper,
} from 'legacy-packages/ft-cb';
import {
  STUDENT_TEST_BOOK,
  SCORING_WORKSHEET,
  STUDENT_TEST_BOOK_PREVIEW,
  SCORING_GUIDE,
} from 'legacy/src/constants/assessmentResourceTypes';
import useFetchDownloadPDF from 'chameleon/ui-stack/hooks/use-fetch-download-pdf';
import cn from 'classnames';
import PopOver from 'chameleon/ui-stack/ui/base/popover';
import Button from 'chameleon/ui-stack/ui/base/button';
import ButtonWithIcon from 'chameleon/ui-stack/ui/buttons/button-with-icon';
import { testBookletQuery } from 'chameleon-src/queries';
import {
  assignmentScoringGuideQuery,
  assessmentScoringGuideQuery,
  assessmentTestBookletQuery,
} from 'chameleon/legacy/src/queries';
import { splitLabelAtWordCount } from 'chameleon/ui-stack/ui/assignment-overview/info-panel/action-buttons/utils';
import { LoadingLabel } from '../../../pdf/DownloadButton/paperscorer';
import './AssociatedResources.scss';
import RelatedPdfsAction from './RelatedPdfsAction';

const AssociatedResources = (props) => {
  const {
    resources,
    isCompact,
    wrapInFragment = false,
    isNewInfoPanelSetting = false,
    displayActionMenu = false,
    ...otherProps
  } = props;
  const [isProcessing, setIsProcessing] = useState(true);
  const [signedResources, setSignedResources] = useState([]);
  const TYPE_LABELS = {
    [STUDENT_TEST_BOOK]: 'Test Books',
    [SCORING_WORKSHEET]: 'Scoring Worksheet',
    [STUDENT_TEST_BOOK_PREVIEW]: 'Test Book Sample',
    [SCORING_GUIDE]: 'Scoring Guide',
  };

  const {
    isMCQPPC,
    isFRQPPC,
    isQB,
    isPE,
    resourceGroupActions,
    assessmentId,
    educationPeriod,
  } = otherProps;

  const downloadResource = (res) => {
    if (res && res.resourceUrl) {
      window.open(res.resourceUrl, '_blank');
    }
  };

  if (resources) {
    resources.sort((r1, r2) => {
      const order1 = r1.order || Number.MAX_VALUE;
      const order2 = r2.order || Number.MAX_VALUE;
      return order1 - order2;
    });
  }

  useEffect(() => {
    const signResourcesUrls = async () => {
      const urlPromises = resources.map((r) =>
        getSignedS3ResourceUrl(r.resourceUrl),
      );
      const results = await Promise.all(urlPromises);
      let mappedResources = resources.map((r, index) => ({
        ...r,
        resourceUrl: results[index],
      }));

      if (isNewInfoPanelSetting) {
        const requiredResources = [
          { condition: !isMCQPPC || isQB || isPE, type: STUDENT_TEST_BOOK },
          { condition: true, type: SCORING_GUIDE },
        ];

        requiredResources.forEach(({ condition, type }) => {
          const hasResource = mappedResources.some(
            (resource) => resource.resourceType === type,
          );
          if (!hasResource && condition) {
            mappedResources.push({
              resourceType: type,
              resourceUrl: '',
              order: mappedResources.length + 1,
            });
          }
        });
      }

      setIsProcessing(false);
      setSignedResources(mappedResources);
    };
    signResourcesUrls();
  }, [resources]);
  const { client, assignmentId, masterSubjectId } = otherProps;

  const downloadScoringGuide = useFetchDownloadPDF({
    client,
    query: assignmentId
      ? assignmentScoringGuideQuery
      : assessmentScoringGuideQuery,
    assignmentId,
    assessmentId,
    masterSubjectId,
    getURLFromResponse: (resp) =>
      assignmentId
        ? resp.data.assignment.scoringGuideUrl
        : resp.data.assessment.scoringGuideUrl,
    setIsProcessing,
  });

  const downloadStudentTestBook = useFetchDownloadPDF({
    client,
    query: assignmentId ? testBookletQuery : assessmentTestBookletQuery,
    assignmentId,
    assessmentId,
    masterSubjectId,
    getURLFromResponse: (resp) =>
      assignmentId
        ? resp.data.assignment.testBookletUrl
        : resp.data.assessment.testBookletUrl,
    setIsProcessing,
  });

  const fetchAndDownloadScoringGuide = () => {
    if (isNewInfoPanelSetting || displayActionMenu) {
      downloadScoringGuide();
    }
  };
  const fetchAndDownloadTestBook = () => {
    if (isNewInfoPanelSetting || displayActionMenu) {
      downloadStudentTestBook();
    }
  };

  // FY-35363: Visiblity of the associated resources/actions buttons in Aassignment Overview page (AC.5.e)
  const hideTestBookAndPreview = (r) => {
    if (!isNewInfoPanelSetting) {
      // Only hide STUDENT_TEST_BOOK_PREVIEW when isPaper is true.
      return r.resourceType !== STUDENT_TEST_BOOK_PREVIEW;
    }

    // Conditions to resource types that should be hidden.
    const hideConditions = {
      isPE: [STUDENT_TEST_BOOK_PREVIEW],
      isMCQPPC: [
        STUDENT_TEST_BOOK_PREVIEW,
        STUDENT_TEST_BOOK,
        SCORING_WORKSHEET,
      ],
      isFRQPPC_or_isQB: [STUDENT_TEST_BOOK_PREVIEW, SCORING_WORKSHEET],
    };

    if (isPE) {
      return !hideConditions.isPE.includes(r.resourceType);
    }
    if (isFRQPPC || isQB) {
      return !hideConditions.isFRQPPC_or_isQB.includes(r.resourceType);
    }
    if (isMCQPPC) {
      return !hideConditions.isMCQPPC.includes(r.resourceType);
    }

    // Default case for isNewInfoPanelSetting: hide STUDENT_TEST_BOOK_PREVIEW
    return ![STUDENT_TEST_BOOK_PREVIEW].includes(r.resourceType);
  };
  const renderPopOver = (children) => {
    const { iconName, label, isDisabled } = otherProps;
    const isDisabledClassName = isDisabled ? 'disabled' : 'active';
    return (
      <PopOver
        hideArrow
        trigger={(buttonProps, _, ref) => (
          <ButtonWithIcon
            {...buttonProps}
            ref={ref}
            className="icon-trigger px-0 mx-0"
            iconClassName={isDisabledClassName}
            icon={iconName}
            iconAccessibility={{
              'aria-label': label,
            }}
            iconOrientation="center"
            disabled={isDisabled}
            isNaked
          >
            {splitLabelAtWordCount(label, isDisabledClassName)}
          </ButtonWithIcon>
        )}
      >
        <ul className="p-0 m-0">{children}</ul>
      </PopOver>
    );
  };

  const handleResourceClick = (resourceType, res) => {
    const isToDowloadScoringGuide =
      resourceType === SCORING_GUIDE && !res?.resourceUrl;
    const isToDownloadTestBooks =
      resourceType === STUDENT_TEST_BOOK && !res?.resourceUrl;

    if (isToDowloadScoringGuide) {
      fetchAndDownloadScoringGuide();
    } else if (isToDownloadTestBooks) {
      fetchAndDownloadTestBook();
    } else {
      downloadResource(res);
    }
  };

  const filteredResources = signedResources?.filter(hideTestBookAndPreview);

  const renderResourceItem = (res, resourceType, index) => {
    const buttonProps = {
      ariaLabel: TYPE_LABELS[resourceType],
      onClick: () => handleResourceClick(resourceType, res),
    };

    const hasNextResource = index + 1 < filteredResources.length;

    return (
      <li
        className={`list-none p-0 m-0 w-full py-1.5 ${
          hasNextResource
            ? cn({ 'border-b border-gray-400': isNewInfoPanelSetting })
            : ''
        }`}
      >
        {isNewInfoPanelSetting ? (
          <ButtonWithIcon
            {...buttonProps}
            className="resourceItem disabled flex items-start justify-start"
            icon="download_sheet"
            iconWidth="24"
            isNaked
          >
            {TYPE_LABELS[resourceType]}
          </ButtonWithIcon>
        ) : (
          <Button isSecondary {...buttonProps}>
            <LegacyIcon name="download" size="18px" />
            {TYPE_LABELS[resourceType]}
          </Button>
        )}
      </li>
    );
  };
  const Component = (
    <Fragment>
      {isProcessing && <LoadingLabel />}
      {!isProcessing &&
        filteredResources?.map((res, index) => {
          const { resourceType } = res;
          return isCompact ? (
            <div
              className={isNewInfoPanelSetting ? 'resourcesContainer' : ''}
              key={`${resourceType}`}
            >
              {renderResourceItem(res, resourceType, index)}
            </div>
          ) : (
            <AccessibilityWrapper key={`${resourceType}`}>
              <div
                aria-label={TYPE_LABELS[resourceType]}
                onClick={() => downloadResource(res)}
                className="associate_resources_button"
                role="button"
                tabIndex={0}
              >
                <span className="secure_icon">
                  <LegacyIcon name="secure_no_circle" size="1.2em" />
                </span>
                <div className="resource_type">{TYPE_LABELS[resourceType]}</div>
              </div>
            </AccessibilityWrapper>
          );
        })}
    </Fragment>
  );

  if (displayActionMenu) {
    const relatedPdfsActionProps = {
      isProcessing,
      resourceGroupActions,
      isMCQPPC,
      resources: signedResources,
      handleResourceClick,
      downloadStudentTestBook,
      downloadScoringGuide,
      assessmentId,
      educationPeriod,
      client,
    };

    return <RelatedPdfsAction {...relatedPdfsActionProps} />;
  }

  if (wrapInFragment) {
    return (
      <Fragment>
        {isNewInfoPanelSetting && renderPopOver(Component)}
        {!isNewInfoPanelSetting && Component}
      </Fragment>
    );
  }

  return (
    <div
      className={
        isCompact ? 'AssociatedResources--compact' : 'AssociatedResources'
      }
      data-test-associated-resources
    >
      {Component}
    </div>
  );
};

AssociatedResources.displayName = 'AssociatedResources';

AssociatedResources.propTypes = {
  resources: PropTypes.array,
  associatedResources: PropTypes.array,
  resourceGroupActions: PropTypes.array,
  isPaper: PropTypes.bool,
};

AssociatedResources.defaultProps = {
  resources: [],
  associatedResources: [],
  resourceGroupActions: [],
  isPaper: true,
};
export default AssociatedResources;
